import { FormikProvider, useFormik } from 'formik';
import { useAuth } from 'react-oidc-context';
import * as yup from 'yup';
import PasswordField from '@/modules/auth/components/PasswordField.tsx';
import { useSignUpUserMutation } from '@/modules/auth/queries/user.tsx';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import { NAME_REGEX } from '@/modules/common/utils/regex.ts';
import { Button, TextInputField } from 'ui';
import { GoogleIcon } from 'ui/icons';
import { SignUpUserRequest } from 'api';

interface ConfirmationPassword {
  confirm_password: string;
}

const validationSchema = yup.object<SignUpUserRequest & ConfirmationPassword>({
  email: yup.string().trim().email('Must be a valid email').required('This field is required'),
  first_name: yup
    .string()
    .trim()
    .min(2, 'Must be at least 2 characters')
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .required('This field is required'),
  last_name: yup
    .string()
    .trim()
    .min(2, 'Must be at least 2 characters')
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .required('This field is required'),
  password: yup
    .string()
    .trim()
    .required('This field is required')
    .matches(
      /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[\W_]).+$/,
      'Password must contain at least 1 letter, 1 digit, and 1 special character.',
    ),
  confirm_password: yup
    .string()
    .trim()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('This field is required'),
});

interface SignUpContentBlockProps {
  redirect_url: string;
  ssoRedirectUrl?: string;
}

const SignUpContentBlock = ({ redirect_url, ssoRedirectUrl }: SignUpContentBlockProps) => {
  const auth = useAuth();

  const handleGoogleSignUp = () => {
    if (ssoRedirectUrl) {
      localStorage.setItem('sso_invitation_signup', 'true');
    }
    auth.signinRedirect({
      redirect_uri: ssoRedirectUrl ?? redirect_url,
      extraQueryParams: { kc_idp_hint: ssoRedirectUrl ? 'google_signup' : 'google' },
    });
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      confirm_password: '',
      redirect_url,
    },
    validationSchema,
    validateOnChange: false,
    validateOnMount: false,
    validateOnBlur: false,
    onSubmit: async values => {
      await mutateAsync(values);
    },
  });

  const { mutateAsync } = useSignUpUserMutation({
    setErrors: formik.setErrors,
    onSuccess: data => {
      window.open(data.session, '_self');
    },
  });

  return (
    <FormikProvider value={formik}>
      <div className="w-full flex flex-col justify-center align-top">
        <form onSubmit={preventFormDefault}>
          <div className="w-full flex flex-col align-middle justify-center gap-8">
            <div className="w-full flex gap-x-2 gap-y-8 flex-wrap lg:flex-nowrap align-middle justify-between">
              <TextInputField name="first_name" label="First Name" />
              <TextInputField name="last_name" label="Last Name" />
            </div>
            <TextInputField
              name="email"
              label="Email"
              placeholder="email@example.com"
              type="email"
            />
            <PasswordField name="password" label="Password" placeholder="Password" />
            <PasswordField name="confirm_password" label="repeat password" placeholder="Password" />
          </div>
          <Button
            className="mt-10 w-full"
            variant="primary"
            onClick={formik.handleSubmit}
            loading={formik.isSubmitting}
            disabled={!formik.dirty || formik.isSubmitting}
          >
            Create an account
          </Button>
          <div className="flex items-center gap-3 mt-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          <Button
            className="mt-4 w-full flex items-center justify-center gap-3"
            variant="secondary"
            onClick={handleGoogleSignUp}
          >
            <GoogleIcon />
            Sign up with Google
          </Button>
        </form>
      </div>
    </FormikProvider>
  );
};

export default SignUpContentBlock;
